<template>
  <section class="py-12">
    <div class="container-wrap">
      <p class="text-sm font-bold text-gray-600">
        {{ tGlobal('products.section-name.resources') }}
      </p>
      <h2 class="text-h2 mb-6">{{ t('title') }}</h2>

      <ul class="column-321">
        <li v-for="item in contextData" :key="item.title">
          <ResourcesCard :item-data="item" />
        </li>
      </ul>

      <div class="flex justify-center pt-8 desktop:pb-8">
        <TheNuxtLink
          :to="`/${locale}/marketplace/news`"
          :title="t('see-all-button')"
          class="flex items-center font-bold text-blue-400 hover:text-blue-500"
        >
          <span class="mr-1">{{ t('see-all-button') }}</span>
          <IconCaretDouble direction="right" />
        </TheNuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { IResource } from '@/components/Area/Products/ResourcesCard.vue';
  import ResourcesCard from '@/components/Area/Products/ResourcesCard.vue';

  const { t, locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });

  const { blobDomain } = useWebsite();
  const { provideResponsiveImgName } = useImagePath();

  const contextData = computed((): IResource[] => [
    {
      imageUrl: `${blobDomain.value}/wise-paas-marketplace/product/${provideResponsiveImgName('resources-item-1')}`,
      title: t('item-1-title'),
      description: t('item-1-description'),
      link: `/${locale.value}/marketplace/news?type=industryFocuses`,
    },
    {
      imageUrl: `${blobDomain.value}/wise-paas-marketplace/product/${provideResponsiveImgName('resources-item-2')}`,
      title: t('item-2-title'),
      description: t('item-2-description'),
      link: `/${locale.value}/marketplace/webinars`,
    },
    {
      imageUrl: `${blobDomain.value}/wise-paas-marketplace/product/${provideResponsiveImgName('resources-item-3')}`,
      title: t('item-3-title'),
      description: t('item-3-description'),
      link: `/${locale.value}/marketplace/news?type=catalogs`,
    },
  ]);
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Resources",
    "item-1-title": "Industry Insights & Trends",
    "item-1-description": "Stay ahead with expert analysis, market news, and the latest updates.",
    "item-2-title": "Events & Webinars",
    "item-2-description": "Join partner forums, product launches, and technical workshops to drive innovation and business growth.",
    "item-3-title": "Catalogs & eBooks",
    "item-3-description": "Deep dive into digital product guides, use cases, and magazines.",
    "see-all-button": "See All Resources"
  },
  "en-jp": {
    "title": "Resources",
    "item-1-title": "Industry Insights & Trends",
    "item-1-description": "Stay ahead with expert analysis, market news, and the latest updates.",
    "item-2-title": "Events & Webinars",
    "item-2-description": "Join partner forums, product launches, and technical workshops to drive innovation and business growth.",
    "item-3-title": "Catalogs & eBooks",
    "item-3-description": "Deep dive into digital product guides, use cases, and magazines.",
    "see-all-button": "See All Resources"
  },
  "en-tw": {
    "title": "Resources",
    "item-1-title": "Industry Insights & Trends",
    "item-1-description": "Stay ahead with expert analysis, market news, and the latest updates.",
    "item-2-title": "Events & Webinars",
    "item-2-description": "Join partner forums, product launches, and technical workshops to drive innovation and business growth.",
    "item-3-title": "Catalogs & eBooks",
    "item-3-description": "Deep dive into digital product guides, use cases, and magazines.",
    "see-all-button": "See All Resources"
  },
  "en-kr": {
    "title": "Resources",
    "item-1-title": "Industry Insights & Trends",
    "item-1-description": "Stay ahead with expert analysis, market news, and the latest updates.",
    "item-2-title": "Events & Webinars",
    "item-2-description": "Join partner forums, product launches, and technical workshops to drive innovation and business growth.",
    "item-3-title": "Catalogs & eBooks",
    "item-3-description": "Deep dive into digital product guides, use cases, and magazines.",
    "see-all-button": "See All Resources"
  },
  "en-americas": {
    "title": "Resources",
    "item-1-title": "Industry Insights & Trends",
    "item-1-description": "Stay ahead with expert analysis, market news, and the latest updates.",
    "item-2-title": "Events & Webinars",
    "item-2-description": "Join partner forums, product launches, and technical workshops to drive innovation and business growth.",
    "item-3-title": "Catalogs & eBooks",
    "item-3-description": "Deep dive into digital product guides, use cases, and magazines.",
    "see-all-button": "See All Resources"
  },
  "en-asia-pacific": {
    "title": "Resources",
    "item-1-title": "Industry Insights & Trends",
    "item-1-description": "Stay ahead with expert analysis, market news, and the latest updates.",
    "item-2-title": "Events & Webinars",
    "item-2-description": "Join partner forums, product launches, and technical workshops to drive innovation and business growth.",
    "item-3-title": "Catalogs & eBooks",
    "item-3-description": "Deep dive into digital product guides, use cases, and magazines.",
    "see-all-button": "See All Resources"
  },
  "en-emea": {
    "title": "Resources",
    "item-1-title": "Industry Insights & Trends",
    "item-1-description": "Stay ahead with expert analysis, market news, and the latest updates.",
    "item-2-title": "Events & Webinars",
    "item-2-description": "Join partner forums, product launches, and technical workshops to drive innovation and business growth.",
    "item-3-title": "Catalogs & eBooks",
    "item-3-description": "Deep dive into digital product guides, use cases, and magazines.",
    "see-all-button": "See All Resources"
  },
  "ja-jp": {
    "title": "リソース",
    "item-1-title": "業界インサイトとトレンド",
    "item-1-description": "専門家の分析や市場ニュースで、常に最新情報を把握。",
    "item-2-title": "イベントとウェビナー",
    "item-2-description": "パートナーフォーラム、新製品発表、技術ワークショップに参加し、ビジネスの革新と成長を加速させる。",
    "item-3-title": "カタログ・eBook",
    "item-3-description": "デジタル製品ガイド、活用事例、マガジンを徹底解剖。",
    "see-all-button": "すべてのリソースを見る"
  },
  "zh-cn": {
    "title": "资源",
    "item-1-title": "产业洞察与趋势",
    "item-1-description": "掌握专家分析、市场新闻及最新动态，保持领先优势。",
    "item-2-title": "活动与研讨会",
    "item-2-description": "立即参与合作伙伴论坛、新品发布、行业研讨会及技术工作坊，驱动企业创新与增长。",
    "item-3-title": "产品目录与电子书",
    "item-3-description": "深入探究数字产品指南、应用案例与行业专刊。",
    "see-all-button": "查看所有资源"
  },
  "zh-tw": {
    "title": "資源",
    "item-1-title": "產業洞察與趨勢",
    "item-1-description": "掌握專家分析、市場新聞及最新動態，保持領先優勢。",
    "item-2-title": "活動與研討會",
    "item-2-description": "立即參與夥伴論壇、新品發佈、與技術工作坊，驅動企業創新與成長。",
    "item-3-title": "產品型錄與電子書",
    "item-3-description": "深入探索數位產品指南、應用案例與產業專刊。",
    "see-all-button": "查看所有資源"
  }
}
</i18n>
